import FactoryGuy from 'ember-data-factory-guy'
import {
  reLessonInfoReading,
  reLessonInfoSpelling,
  reLessonInfoStorylands,
  reLessonInfoDrivingTests,
  rexLessonInfoMyLesson,
  rexLessonInfoSpelling,
  fpLessonInfoLessons,
  mathsLessonInfoLessons,
  mathsDrivingTestsLessons,
  mathsMentalMinuteLessons,
  wlLessonInfoWriting,
} from 'teacher-ui-client/mirage/fixtures/lesson-info'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import type { LessonInfoCache, Lesson } from 'teacher-ui-client/services/lesson-info'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import { range } from 'lodash'
import { http } from 'msw'

function buildResponse(lessonInfo: unknown) {
  return new Response(JSON.stringify(lessonInfo), { status: 200, headers: { 'content-type': 'application/json' } })
}

export function mockLessonInfoRequest(responseBody?: unknown) {
  const handlers = [
    http.get(join(config.lessonsApiV1Url, 're', 'lesson_info').href, ({ request }) => {
      if (responseBody) return buildResponse(responseBody)

      const requestUrl = new URL(request.url)

      const productAndPrecinct = `${requestUrl.searchParams.get('product')} ${requestUrl.searchParams.get('precinct')}`

      switch (productAndPrecinct) {
        case 're lessons':
          return buildResponse(reLessonInfoReading)
        case 're spelling':
          return buildResponse(reLessonInfoSpelling)
        case 're storylands':
          return buildResponse(reLessonInfoStorylands)
        case 're driving_tests':
          return buildResponse(reLessonInfoDrivingTests)
        default:
          return buildResponse(null)
      }
    }),
    http.get(join(config.lessonsApiV1Url, 'rex', 'lesson_info').href, ({ request }) => {
      if (responseBody) return buildResponse(responseBody)

      const requestUrl = new URL(request.url)

      const productAndPrecinct = `${requestUrl.searchParams.get('product')} ${requestUrl.searchParams.get('precinct')}`

      switch (productAndPrecinct) {
        case 'rex my_lessons':
          return buildResponse(rexLessonInfoMyLesson)
        case 'rex english_skills_spelling':
          return buildResponse(rexLessonInfoSpelling)
        default:
          return buildResponse(null)
      }
    }),
    http.get(join(config.lessonsApiV1Url, 'fast_phonics', 'lesson_info').href, ({ request }) => {
      if (responseBody) return buildResponse(responseBody)

      const requestUrl = new URL(request.url)

      const productAndPrecinct = `${requestUrl.searchParams.get('product')} ${requestUrl.searchParams.get('precinct')}`

      switch (productAndPrecinct) {
        case 'fast_phonics lessons':
          return buildResponse(fpLessonInfoLessons)
        default:
          return buildResponse(null)
      }
    }),
    http.get(join(config.lessonsApiV1Url, 'maths', 'lesson_info').href, ({ request }) => {
      if (responseBody) return buildResponse(responseBody)

      const requestUrl = new URL(request.url)

      const productAndPrecinct = `${requestUrl.searchParams.get('product')} ${requestUrl.searchParams.get('precinct')}`

      switch (productAndPrecinct) {
        case 'maths lessons':
          return buildResponse(mathsLessonInfoLessons)
        case 'maths driving_tests':
          return buildResponse(mathsDrivingTestsLessons)
        case 'maths mental_minute':
          return buildResponse(mathsMentalMinuteLessons)
        default:
          return buildResponse(null)
      }
    }),
    http.get(join(config.lessonsApiV1Url, 'writing_legends', 'lesson_info').href, ({ request }) => {
      if (responseBody) return buildResponse(responseBody)

      const requestUrl = new URL(request.url)

      const productAndPrecinct = `${requestUrl.searchParams.get('product')} ${requestUrl.searchParams.get('precinct')}`

      switch (productAndPrecinct) {
        case 'writing_legends writing':
          return buildResponse(wlLessonInfoWriting)
        default:
          return buildResponse(null)
      }
    }),
  ]
  FactoryGuy.requestManager.worker.use(...handlers)
}

// reset to 1 when grade reached. This logic is a way to do modulo but resetting at 1 not 0.
export function gradeLessonNumber(lessonPos: number, lessonsPerGrade: number) {
  return Math.floor(lessonPos % lessonsPerGrade || lessonsPerGrade)
}

export function generateLessonInfoCacheFor(precincts: Precinct[]) {
  return precincts.reduce<Partial<LessonInfoCache>>((acc, precinct) => {
    const lessons = range(1, 500).map((lessonPos, index) => {
      if (
        precinct === Precinct.RE_READING ||
        precinct === Precinct.RE_STORYLANDS ||
        precinct === Precinct.REX_READING ||
        precinct === Precinct.REX_SPELLING ||
        precinct === Precinct.MS_LESSONS
      ) {
        // Taking a shortcut to define them the same, since their types are currently the same, but if that changes or types cause issues, define them separately.
        return {
          id: `[lesson ${lessonPos} id]`,
          name: `[lesson ${lessonPos} name]`,
          map: Math.ceil(lessonPos / 10),
          lesson_number: `[lesson ${lessonPos} number]`,
          lesson_position: lessonPos,
          grade: Math.floor(index / 10),
          grade_position: Math.floor(index / 10),
          grade_lesson_number: gradeLessonNumber(lessonPos, 10),
        } satisfies Lesson<typeof Precinct.RE_READING> &
          Lesson<typeof Precinct.RE_STORYLANDS> &
          Lesson<typeof Precinct.REX_READING> &
          Lesson<typeof Precinct.REX_SPELLING> &
          Lesson<typeof Precinct.MS_LESSONS>
      }
      if (precinct === Precinct.RE_SPELLING) {
        return {
          id: `[lesson ${lessonPos} id]`,
          name: `[lesson ${lessonPos} name]`,
          map: Math.ceil(lessonPos / 10),
          lesson_number: `[lesson ${lessonPos} number]`,
          lesson_position: lessonPos,
          grade: Math.floor(index / 10),
          grade_position: Math.floor(index / 10),
          grade_lesson_number: gradeLessonNumber(lessonPos, 10),
          description: `[lesson ${lessonPos} description]`,
        } satisfies Lesson<typeof Precinct.RE_SPELLING>
      }
      if (precinct === Precinct.RE_DRIVING_TESTS) {
        /* RE DT is a little special. Lesson position / number appear multiple times, but with different categories.
         * ie
         * { lesson_position: 1, category: 'sight_words' }
         * { lesson_position: 1, category: 'letters_and_sounds' }
         * { lesson_position: 1, category: 'content_words' }
         * { lesson_position: 2, category: 'sight_words' }
         * { lesson_position: 2, category: 'letters_and_sounds' }
         * { lesson_position: 2, category: 'content_words' }
         * ...
         */
        const categoryLessonPos = Math.ceil(lessonPos / 3)
        return {
          id: `[lesson ${categoryLessonPos} id]`,
          name: `[lesson ${categoryLessonPos} name]`,
          lesson_number: `[lesson ${categoryLessonPos} number]`,
          lesson_position: categoryLessonPos,
          grade: Math.floor(index / 9),
          grade_position: Math.floor(index / 9),
          grade_lesson_number: gradeLessonNumber(categoryLessonPos, 3),
          category: ['sight_words' as const, 'letters_and_sounds' as const, 'content_words' as const][index % 3],
        } satisfies Lesson<typeof Precinct.RE_DRIVING_TESTS>
      }
      if (precinct === Precinct.MS_DRIVING_TESTS) {
        const categoryLessonPos = Math.ceil(lessonPos / 6)
        return {
          id: `[lesson ${categoryLessonPos} id]`,
          name: `[lesson ${categoryLessonPos} name]`,
          lesson_number: `[lesson ${categoryLessonPos} number]`,
          lesson_position: categoryLessonPos,
          grade: Math.floor(index / 18),
          grade_position: Math.floor(index / 18),
          grade_lesson_number: gradeLessonNumber(categoryLessonPos, 6),
          category: [
            'data' as const,
            'geometry' as const,
            'measurement' as const,
            'number' as const,
            'operations' as const,
            'patterns' as const,
          ][index % 6],
        } satisfies Lesson<typeof Precinct.MS_DRIVING_TESTS>
      }
      if (precinct === Precinct.MS_MENTAL_MINUTE) {
        return {
          id: `[lesson ${lessonPos} id]`,
          name: `[lesson ${lessonPos} name]`,
          lesson_number: lessonPos,
          lesson_position: lessonPos,
          // lessons 1-4 Addition, lessons 5-8 Subtraction, ...
          category: ['Addition' as const, 'Subtraction' as const, 'Multiplication' as const, 'Division' as const][
            Math.floor(index / 4) % 4
          ],
          grade: Math.floor(index / 40),
        } satisfies Lesson<typeof Precinct.MS_MENTAL_MINUTE>
      }
      if (precinct === Precinct.FP_PEAKS) {
        return {
          id: lessonPos,
          name: `[lesson ${lessonPos} name]`,
          lesson_number: lessonPos,
          peak: lessonPos,
          set: lessonPos,
          phase: Math.ceil(lessonPos / 4),
          sounds: [`[lesson ${lessonPos} sound_A]`, `[lesson ${lessonPos} sound_B]`],
          words: [`[lesson ${lessonPos} word_1]`, `[lesson ${lessonPos} word_2]`],
          books: [`[lesson ${lessonPos} book_1]`, `[lesson ${lessonPos} book_2]`],
          description: `[lesson ${lessonPos} description]`,
        } satisfies Lesson<typeof Precinct.FP_PEAKS>
      }
      if (precinct === Precinct.WL_WRITING) {
        return {
          lesson: lessonPos,
          lesson_number: lessonPos,
          map_number: Math.ceil(lessonPos / 5),
        } satisfies Lesson<typeof Precinct.WL_WRITING>
      }
      throw new Error('no lesson definition for chosen precinct')
    })
    return { ...acc, [precinct]: { lessons } }
  }, {})
}
